
import React, { useEffect, useMemo, useState } from "react";
import {
  Edit2,
  Trash2,
  Check,
  X,
  Upload,
  User,
  Briefcase,
  GraduationCap,
  AlignLeft,
  AlignRight,
  AlignCenter,
  Tag,
  Search,
  Star,
} from "lucide-react";
import API_BASE_URL from "../../../services/api";

const API = `${API_BASE_URL}/api/esignatures`;

export default function ESignatureManagement() {
  const [list, setList] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [editId, setEditId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [form, setForm] = useState({
    name: "",
    qualification: "",
    designation: "",
    alignment: "LEFT",
    departments: [],
    defaultDepartmentIds: [],
    signatureImg: null,
  });

  useEffect(() => {
    (async () => {
      try {
        await Promise.all([fetchList(), fetchDepartments()]);
      } catch (e) {
        console.error(e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // cleanup preview blob URL
  useEffect(() => {
    return () => {
      try {
        if (preview?.startsWith("blob:")) URL.revokeObjectURL(preview);
      } catch {}
    };
  }, [preview]);

  // =========================
  // API CALLS
  // =========================
  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await fetch(API);
      const data = await res.json();
      const items = Array.isArray(data) ? data : data?.data || [];
      setList(items);
    } catch (err) {
      console.error("Error loading signatures", err);
      setError("Failed to load signatures");
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/departments`);
      const data = await res.json();

      const items = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
        ? data.items
        : data?.data || [];

      setDepartments((items || []).filter((d) => d?.isActive !== false));
    } catch (err) {
      console.error("Failed to load departments", err);
      setError("Failed to load departments");
    }
  };

  // =========================
  // HELPERS
  // =========================
  const resetForm = () => {
    setForm({
      name: "",
      qualification: "",
      designation: "",
      alignment: "LEFT",
      departments: [],
      defaultDepartmentIds: [],
      signatureImg: null,
    });
    setPreview(null);
    setEditId(null);
    setError("");
    setSuccess("");
  };

  const handleDepartmentToggle = (id) => {
    const depId = Number(id);
    setForm((prev) => {
      const exists = prev.departments.includes(depId);
      const nextDepartments = exists
        ? prev.departments.filter((d) => d !== depId)
        : [...prev.departments, depId];

      // if department removed, remove from defaults too
      const nextDefaults = exists
        ? prev.defaultDepartmentIds.filter((d) => d !== depId)
        : prev.defaultDepartmentIds;

      return { ...prev, departments: nextDepartments, defaultDepartmentIds: nextDefaults };
    });
  };

  const handleDefaultToggle = (id) => {
    const depId = Number(id);
    setForm((prev) => {
      if (!prev.departments.includes(depId)) return prev;

      const isDefault = prev.defaultDepartmentIds.includes(depId);
      const nextDefaults = isDefault
        ? prev.defaultDepartmentIds.filter((d) => d !== depId)
        : [...prev.defaultDepartmentIds, depId];

      return { ...prev, defaultDepartmentIds: nextDefaults };
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    // cleanup old blob preview
    if (preview?.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(preview);
      } catch {}
    }

    setForm((prev) => ({ ...prev, signatureImg: file || null }));
    setPreview(file ? URL.createObjectURL(file) : null);
  };

  const AlignmentIcon = ({ alignment }) => {
    if (alignment === "LEFT") return <AlignLeft size={13} />;
    if (alignment === "RIGHT") return <AlignRight size={13} />;
    return <AlignCenter size={13} />;
  };

  const selectedDepartmentObjects = useMemo(() => {
    const setIds = new Set((form.departments || []).map(Number));
    return (departments || []).filter((d) => setIds.has(Number(d.id)));
  }, [departments, form.departments]);

  // =========================
  // SUBMIT
  // =========================
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setError("");
    setSuccess("");

    try {
      if (!form?.name?.trim()) {
        setError("Name is required");
        return;
      }

      const selectedSet = new Set((form.departments || []).map(Number));
      const cleanedDefaultIds = (form.defaultDepartmentIds || [])
        .map(Number)
        .filter((id) => selectedSet.has(id));

      const fd = new FormData();
      fd.append("name", form.name.trim());
      fd.append("qualification", form.qualification || "");
      fd.append("designation", form.designation || "");
      fd.append("alignment", form.alignment || "LEFT");

      fd.append("departments", (form.departments || []).join(","));

      // ✅ IMPORTANT: always send defaults field on update
      // - if none selected, send "" to clear
      if (editId) {
        fd.append("defaultDepartmentIds", cleanedDefaultIds.join(",")); // can be ""
      } else {
        // create: only send if any defaults selected
        if (cleanedDefaultIds.length) fd.append("defaultDepartmentIds", cleanedDefaultIds.join(","));
      }

      if (form.signatureImg) fd.append("signatureImg", form.signatureImg);

      const url = editId ? `${API}/${editId}` : API;
      const method = editId ? "PUT" : "POST";

      const res = await fetch(url, { method, body: fd });
      const data = await res.json();

      if (!res.ok) {
        setError(data?.error || data?.message || "Failed to save");
      } else {
        setSuccess(editId ? "Updated" : "Added");
        await fetchList();
        resetForm();
      }
    } catch (err) {
      console.error(err);
      setError("Connection error");
    } finally {
      setSubmitLoading(false);
      setTimeout(() => setSuccess(""), 2800);
    }
  };

  // =========================
  // EDIT / DELETE
  // =========================
  const handleEdit = (item) => {
    setEditId(item.id);

    const depIds = item?.departments?.map((x) => Number(x.departmentItemId)) || [];
    const defaultIds =
      item?.departments?.filter((x) => x?.isDefault).map((x) => Number(x.departmentItemId)) || [];

    setForm({
      name: item?.name || "",
      qualification: item?.qualification || "",
      designation: item?.designation || "",
      alignment: item?.alignment || "LEFT",
      departments: depIds,
      defaultDepartmentIds: defaultIds,
      signatureImg: null,
    });

    setPreview(item?.signatureImg || null);
    setError("");
    setSuccess("");
  };

  const handleDelete = async (id) => {
    const ok = window.confirm("Delete this signature?");
    if (!ok) return;

    try {
      const res = await fetch(`${API}/${id}`, { method: "DELETE" });
      if (!res.ok) {
        setError("Delete failed");
        return;
      }
      setSuccess("Deleted");
      await fetchList();
    } catch (err) {
      console.error(err);
      setError("Delete failed");
    } finally {
      setTimeout(() => setSuccess(""), 2800);
    }
  };

  // =========================
  // SEARCH
  // =========================
  const filteredList = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return list;

    return (list || []).filter((item) => {
      const name = String(item?.name || "").toLowerCase();
      const des = String(item?.designation || "").toLowerCase();
      const qua = String(item?.qualification || "").toLowerCase();

      const depMatch =
        item?.departments?.some((d) =>
          String(d?.departmentItem?.name || "").toLowerCase().includes(term)
        ) || false;

      return name.includes(term) || des.includes(term) || qua.includes(term) || depMatch;
    });
  }, [list, searchTerm]);

  // =========================
  // UI
  // =========================
  return (
    <div className="max-w-screen-2xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* FORM */}
        <div className="lg:col-span-4 xl:col-span-3">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
            <div className="flex items-center justify-between mb-3 pb-2 border-b">
              <h2 className="text-sm font-semibold text-gray-800">
                {editId ? "Edit Signature" : "New Signature"}
              </h2>

              {editId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-xs text-gray-500 hover:text-gray-700"
                >
                  Cancel
                </button>
              )}
            </div>

            {success ? (
              <div className="mb-3 p-2 bg-green-50 border border-green-200 rounded text-green-700 text-xs flex items-center gap-1.5">
                <Check size={14} /> {success}
              </div>
            ) : null}

            {error ? (
              <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-xs flex items-center gap-1.5">
                <X size={14} /> {error}
              </div>
            ) : null}

            <form onSubmit={handleSubmit} className="space-y-3 text-sm">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <User size={12} /> Full Name *
                </label>
                <input
                  className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  required
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <GraduationCap size={12} /> Qualification
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={form.qualification}
                    onChange={(e) => setForm((p) => ({ ...p, qualification: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                    <Briefcase size={12} /> Designation
                  </label>
                  <input
                    className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    value={form.designation}
                    onChange={(e) => setForm((p) => ({ ...p, designation: e.target.value }))}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <AlignmentIcon alignment={form.alignment} /> Alignment
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2.5 py-1.5 text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white"
                  value={form.alignment}
                  onChange={(e) => setForm((p) => ({ ...p, alignment: e.target.value }))}
                >
                  <option value="LEFT">Left</option>
                  <option value="CENTER">Center</option>
                  <option value="RIGHT">Right</option>
                </select>
              </div>

              {/* Departments */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Tag size={12} /> Departments
                </label>

                <div className="max-h-32 overflow-y-auto border border-gray-200 rounded p-2 bg-gray-50 text-xs">
                  {departments.length === 0 ? (
                    <div className="text-[11px] text-gray-500 px-1 py-1">No departments found</div>
                  ) : (
                    departments.map((dep) => (
                      <label
                        key={dep.id}
                        className="flex items-center gap-1.5 py-1 px-1.5 hover:bg-gray-100 rounded cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          className="h-3.5 w-3.5 text-blue-600 rounded"
                          checked={form.departments.includes(Number(dep.id))}
                          onChange={() => handleDepartmentToggle(dep.id)}
                        />
                        <span>
                          {dep.name}
                          {dep?.type ? (
                            <span className="text-[10px] text-gray-500 ml-1">
                              ({String(dep.type).toLowerCase()})
                            </span>
                          ) : null}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              {/* Default Departments */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Star size={12} /> Default Departments (multiple)
                </label>

                <div className="border border-gray-200 rounded p-2 bg-white">
                  {selectedDepartmentObjects.length === 0 ? (
                    <div className="text-[11px] text-gray-500">Select departments first.</div>
                  ) : (
                    <div className="max-h-28 overflow-y-auto text-xs">
                      {selectedDepartmentObjects.map((d) => {
                        const isDef = form.defaultDepartmentIds.includes(Number(d.id));
                        return (
                          <label
                            key={`def-${d.id}`}
                            className={`flex items-center justify-between gap-2 py-1 px-1.5 rounded cursor-pointer hover:bg-gray-50 ${
                              isDef ? "bg-green-50" : ""
                            }`}
                          >
                            <span className="text-gray-700">{d.name}</span>
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 text-green-600 rounded"
                              checked={isDef}
                              onChange={() => handleDefaultToggle(d.id)}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}

                  {form.defaultDepartmentIds.length ? (
                    <div className="mt-2 text-[10px] text-gray-500">
                      Defaults selected:{" "}
                      <span className="text-gray-700 font-medium">
                        {form.defaultDepartmentIds.length}
                      </span>
                    </div>
                  ) : null}

                  <div className="mt-1 text-[10px] text-gray-400">
                    Note: One default per alignment slot (LEFT / CENTER / RIGHT) per department. Different alignments can each have a different default.
                  </div>
                </div>
              </div>

              {/* Upload */}
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                  <Upload size={12} /> Signature Image
                </label>

                <div className="border-2 border-dashed border-gray-300 rounded p-3 text-center hover:border-blue-400 transition-colors">
                  <input
                    type="file"
                    accept="image/png,image/jpeg"
                    onChange={handleFileChange}
                    className="hidden"
                    id="sig-upload"
                  />
                  <label htmlFor="sig-upload" className="cursor-pointer text-xs text-blue-600 hover:text-blue-800">
                    Choose file
                  </label>
                  <p className="text-[10px] text-gray-500 mt-1">180×60 px recommended</p>
                </div>

                {preview ? (
                  <div className="mt-2 p-2 border border-gray-200 rounded bg-gray-50">
                    <p className="text-[10px] text-gray-600 mb-1">Preview:</p>
                    <img
                      src={preview}
                      alt="Signature preview"
                      className="h-10 mx-auto object-contain bg-white border rounded p-1"
                    />
                  </div>
                ) : null}
              </div>

              <button
                type="submit"
                disabled={submitLoading}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium py-2 rounded transition-colors disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {submitLoading ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Saving...
                  </>
                ) : editId ? (
                  "Update"
                ) : (
                  "Add Signature"
                )}
              </button>
            </form>
          </div>
        </div>

        {/* TABLE */}
        <div className="lg:col-span-8 xl:col-span-9">
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
            <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <h2 className="text-sm font-semibold text-gray-800">Signature Directory</h2>

              <div className="relative w-full sm:w-64">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                />
              </div>
            </div>

            {loading ? (
              <div className="py-10 flex flex-col items-center text-gray-500 text-sm">
                <div className="w-7 h-7 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-2" />
                Loading...
              </div>
            ) : filteredList.length === 0 ? (
              <div className="py-12 text-center text-gray-500 text-sm">
                <p className="font-medium">{searchTerm ? "No matching signatures" : "No signatures found"}</p>
                <p className="text-xs mt-1">{searchTerm ? "Try different keywords" : "Add signatures using the form"}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs text-left text-gray-700">
                  <thead className="bg-gray-50 text-gray-600 uppercase tracking-wider">
                    <tr>
                      <th className="px-4 py-2 w-12 text-center">Sl No.</th>
                      <th className="px-4 py-2 w-28">Signature</th>
                      <th className="px-4 py-2">Name & Details</th>
                      <th className="px-4 py-2 w-20">Alignment</th>
                      <th className="px-4 py-2">Departments</th>
                      <th className="px-4 py-2 w-20 text-right">Actions</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-100">
                    {filteredList.map((item, index) => {
                      const defaultDeps =
                        item?.departments?.filter((d) => d?.isDefault).map((d) => d?.departmentItem?.name).filter(Boolean) ||
                        [];

                      const depList =
                        item?.departments?.map((d) => d?.departmentItem?.name).filter(Boolean) || [];

                      return (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors group">
                          <td className="px-4 py-3 text-center text-gray-500">{index + 1}</td>

                          <td className="px-4 py-3">
                            <div className="w-20 h-14 bg-white border border-gray-200 rounded flex items-center justify-center overflow-hidden">
                              <img
                                src={item.signatureImg}
                                alt={item.name}
                                className="max-h-full max-w-full object-contain p-1"
                              />
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-900">{item.name}</div>

                            {defaultDeps.length ? (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {defaultDeps.map((nm, i) => (
                                  <span
                                    key={`${item.id}-def-${i}`}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-green-100 text-green-800 text-[10px] rounded-full"
                                  >
                                    <Star size={10} /> {nm}
                                  </span>
                                ))}
                              </div>
                            ) : null}

                            {item.designation ? <div className="text-gray-600 mt-1">{item.designation}</div> : null}
                            {item.qualification ? (
                              <div className="text-gray-500 text-[10px] mt-0.5">{item.qualification}</div>
                            ) : null}
                          </td>

                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1">
                              <AlignmentIcon alignment={item.alignment} />
                              <span className="capitalize">{String(item.alignment || "").toLowerCase().trim()}</span>
                            </div>
                          </td>

                          <td className="px-4 py-3">
                            {depList.length ? (
                              <div className="flex flex-wrap gap-1">
                                {depList.map((name, i) => (
                                  <span
                                    key={`${item.id}-dep-${i}`}
                                    className="inline-flex px-1.5 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded"
                                  >
                                    {name}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 text-[10px]">—</span>
                            )}
                          </td>

                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1 opacity-70 group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => handleEdit(item)}
                                className="p-1 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                                title="Edit"
                              >
                                <Edit2 size={14} />
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDelete(item.id)}
                                className="p-1 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded"
                                title="Delete"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="px-4 py-2 border-t text-xs text-gray-500 bg-gray-50">
              Showing {filteredList.length} of {list.length} signatures
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}